import dayjs, { type Dayjs } from "dayjs"

import { type Permission } from "@/types"
import { BookingTypeEnum, type Child, BookingTimeCategory } from "@/graphql/types"
import { i18n } from "@/i18n"

import { calculateAge, Month, monthLabels, monthRecord, type Year, YearMode } from "./calendar"
import { Region } from "./commonOptions"
import { hasOverlapOrInside } from "./date"
import { KibigQualification } from "./employmentHelpers"
import {
  Allocation,
  eligibleForParentalFeeSubsidy,
  type KibigChild,
  PreCourse,
} from "./kibig/children"
import { type EmployeeKibigMonths } from "./kibig/employees"
import { isDev } from "./misc"
import {
  baseValue,
  qualityBonus,
  expansionFactor,
  minimumStaffingRatio,
  subsidyForParentalFee,
  SubsidyStateFactors,
  BookingTimeFactors,
  BookingTimeHours,
  SubsidyState,
  BookingTime,
  isIntegrative,
  RegionMFBookingTimeFactorConstantAddend,
  minimumFkq,
  Criteria,
  InfoMessage,
  MonthSubsidyStatus,
  UnmetMonthAllowance,
} from "./subsidy"
import { type WeightingKey } from "./weighting"
import { resolveWeightings, getWeightingKey } from "./weightingChartWidget"

export const requiredAnalysisRoles: readonly Permission[] = [
  "appointment:list",
  "booking:list",
  "child:list",
  "employee:list",
  "employment:list",
  "analysis:unlock",
]

/** https://digitale-verwaltung.atlassian.net/wiki/spaces/adebisKITA/pages/1664679983/Overview+of+Subsidy-Relevant+Data */
export type FundingRelevantRecord = {
  monat: string
  /** Total number of subsidized children */
  kinder: number
  /** Total number of regularily subsidized children */
  regelkinder: number
  /** Total number of school child subsidized children */
  schulkinder: number
  /** Total number of migration background subsidized children */
  migration: number
  /** Total number of U3 or Krippenkind subsidized children */
  u3krippe: number
  /** Number of subsidized children which have `assistedIntegration` set in booking. */
  integrativ: number
  /** Percentage number of Standard-/Regelbuchung with a Buchungszeitfaktor greater than three hours. */
  anteilgr3: number
  /** Sum of the daily booking hours. */
  bustd: number
  /** Sum of the daily booking hours weighted. */
  bustgew: number
  /** Sum of the daily booking hours weighted (under consideration of 4,5 + X factor increase). */
  "bustgew+x": number
  /** Sum of the daily booking hours weighted (under consideration of district subsidy). */
  "bustgew5,5": number
  /** Sum of the weekly working hours from specified Anstellungen. */
  arbstd: number
  /** Sum of the weekly working hours from Anstellungen types that are Fachkräfte. */
  arbstdfk: number
  /** Minimum weekly working hours by Fachkräfte. */
  midstdfk: number | "-"
  /** Minimum weekly working hours by Fachkräfte (under consideration of 4,5 + X factor increase). */
  "midstdfk+x": number | "-"
  /** Minimum weekly working hours by Fachkräfte (under consideration of district subsidy). */
  "midstdfk5,5": number | "-"
  /** Minimum amount of hours that need to be spend by Fachkräfte. */
  fkplusminus: number | "-"
  /** Minimum amount of hours that need to be spend by Fachkräfte (under consideration of 4,5 + X factor increase). */
  "fkplusminus+x": number | "-"
  /** Fachkraftquote. */
  fkq: number | "-"
  "fkq+x": number | "-"
  /** Fachkraftquote (under consideration of district subsidy). */
  "fkq5,5": number | "-"
  /** Anstellungsschlüssel. Amount of children on employee has to care for. */
  as: number | "-"
  /** Anstellungsschlüssel (under consideration of 4,5 + X factor increase). */
  "as+x": number | "-"
  /** Anstellungsschlüssel (under consideration of district subsidy). */
  "as5,5": number | "-"
  /** Indication yes or no if the Förderung will be granted or not. */
  foerderung: MonthSubsidyStatus | string
  /** Amount of money granted by the Förderung. */
  foerderbetrag: number
  /** Additional money granted by using the 4,5 + X factor increase. */
  "+x": number
  /** Amount of money granted including the 4,5 + X factor increase. */
  "foerderbetrag+x": number
  /** Standard-/ Regelbuchung with the Vorkurs checked. */
  vorkurse: number
  /** Zusatzförderung Kinder unter 3 Jahre.  */
  zusatzfoerdu3: number
  /** Qualitätsbonus. */
  qbonus: number
  /** Total amount granted be the Förderung. */
  foerderungges: number
  /** Elternbeitragszuschuss. */
  ebzuschuss: number
  /** Base value for the calculation of the Förderung. Will be adjusted on a yearly basis. */
  basiswert: number
  /** Zuschuss Bundesmittel */
  zuschussBundesmittel: number
  /** Any subsidizability criteria not met within the month */
  criteria?: Criteria[]
  /** Info message for the averages lines */
  info?: InfoMessage
}

export type FundingRelevantDataSummary = {
  base: number
  u3: number
  precourse: number
  quality: number
  vacation: number
  total: number
  parentalFeeSubsidy: number
  federal: number
}

export const TableLabels = {
  averages: i18n.global.t("home.analyse.labels.averages"),
  vacationShortTime: i18n.global.t("home.analyse.labels.vacation_short_time"),
  sum: i18n.global.t("home.analyse.labels.sum:"),
} as const

const MONTH_KEYS = [
  "jan",
  "feb",
  "maerz",
  "apr",
  "mai",
  "juni",
  "juli",
  "aug",
  "sept",
  "okt",
  "nov",
  "dez",
] as const

export const analysisMonthHeaders = MONTH_KEYS.map((key) =>
  i18n.global.t(`home.analyse_models.tabs.overview.details.table.months.${key}`)
)

export function minPaed(record: FundingRelevantRecord | null): number | string {
  if (!record) return "-"
  return (record.bustgew * 5) / minimumStaffingRatio
}

export function paedPlusMinus(record: FundingRelevantRecord | null): number | string {
  const minPedHours = minPaed(record)
  if (typeof minPedHours === "string" || !record) return "-"
  return record.arbstd - minPedHours
}

export function highlightCell(
  record: FundingRelevantRecord | null,
  field: "paedpm" | "fkplusminus"
): "success" | "danger" | null {
  if (!record) return null
  const val = field === "paedpm" ? paedPlusMinus(record) : record.fkplusminus
  if (typeof val !== "number") return null
  if (val > 0) return "success"
  if (val < 0) return "danger"
  return null
}

export enum RuralKita {
  TEN = 10,
  TWENTYFIVE = 25,
}

export const districtSubsidyDistricts = [
  Region.MF,
  Region.NB,
  Region.OF,
  Region.OP,
  Region.SW,
] as const

type ChildRecords = Record<Month, ChildMonth[]>
type EmployeeRecords = Record<Month, EmployeeMonth[]>
// Bag of Options. Choose one or live with the consequences.
type Options = {
  rural?: RuralKita
  factorIncrease?: { plusX: number } | { agBrutto: number }
  districtSubsidy?: Region
}

/**
 * Implements
 *  https://digitale-verwaltung.atlassian.net/wiki/spaces/adebisKITA/pages/1602289672/F+rderberechnung
 * and its subpages.
 */
export function runFundingAnalysis(
  childrenKibigData: KibigChild[],
  employeeKibigData: EmployeeKibigMonths,
  year: number,
  options: Options
) {
  const childRecords = kibigChildrenToAnalysisMonths(childrenKibigData, year)
  const childRecordsWithVacationAndShortTime = kibigChildrenToAnalysisMonths(
    childrenKibigData,
    year,
    true
  )
  const employeeRecords = groupKibigEmployeesByMonths(employeeKibigData, year)

  const factorIncrease: { plusX: number } | { agBrutto: number } | object =
    options.factorIncrease ?? {}
  let plusX = "plusX" in factorIncrease ? factorIncrease.plusX : NaN
  if ("agBrutto" in factorIncrease) {
    plusX = calculatePlusX(factorIncrease.agBrutto, childRecords, year)
  }

  if (plusX) {
    for (let i = 0; i < 12; i++) {
      for (const children of [
        childRecords[i as Month],
        childRecordsWithVacationAndShortTime[i as Month],
      ]) {
        for (const childMonth of children) {
          if (isIntegrative(childMonth.subsidyState)) {
            childMonth.subsidyStateFactorPlusX = childMonth.subsidyStateFactor + plusX
            childMonth.subsidyStateFactorWithoutAssistedIntegrationPlusX =
              childMonth.subsidyStateFactorWithoutAssistedIntegration + plusX
          }
        }
      }
    }
  }

  if (options.districtSubsidy) {
    if (!(districtSubsidyDistricts as readonly Region[]).includes(options.districtSubsidy)) {
      console.warn("Unexpected/invalid district for district subsidy: ", options.districtSubsidy)
    }
    // The Mittelfranken special case is handled within the algorithm
    if (options.districtSubsidy !== Region.MF) {
      for (let i = 0; i < 12; i++) {
        for (const children of [
          childRecords[i as Month],
          childRecordsWithVacationAndShortTime[i as Month],
        ]) {
          for (const childMonth of children) {
            if (childMonth.districtSubsidy && isIntegrative(childMonth.subsidyState)) {
              childMonth.subsidyStateFactorForDistrict += 1
              childMonth.subsidyStateFactorWithoutAssistedIntegrationForDistrict += 1
            }
          }
        }
      }
    }
  }

  const records = calculateFundingRelevantRecords(childRecords, employeeRecords, year, options)
  const recordsWithVacationAndShortTime = calculateFundingRelevantRecords(
    childRecordsWithVacationAndShortTime,
    employeeRecords,
    year,
    options
  )

  const sumsAndAverages = calculateSumsAndAverages(
    records,
    TableLabels.averages,
    InfoMessage.averages_row
  )
  const withVacationAndShortTime = calculateSumsAndAverages(
    recordsWithVacationAndShortTime,
    TableLabels.vacationShortTime,
    InfoMessage.averages_row2
  )
  const summary: FundingRelevantDataSummary = {
    base: sumsAndAverages.foerderbetrag,
    u3: sumsAndAverages.zusatzfoerdu3,
    precourse: sumsAndAverages.vorkurse,
    quality: sumsAndAverages.qbonus,
    vacation: withVacationAndShortTime.foerderungges - sumsAndAverages.foerderungges,
    total: withVacationAndShortTime.foerderungges,
    parentalFeeSubsidy: sumsAndAverages.ebzuschuss,
    federal: sumsAndAverages.zuschussBundesmittel,
  }

  let extras: undefined | { agBrutto: number } | { plusX: number }
  if ("plusX" in factorIncrease) {
    extras = {
      agBrutto: calculateAgBrutto(factorIncrease.plusX, childRecords, year),
    }
  } else if ("agBrutto" in factorIncrease) {
    extras = {
      plusX,
    }
  }
  return {
    records: [...records, sumsAndAverages, withVacationAndShortTime],
    summary,
    extras,
  }
}

function calculateFundingRelevantRecords(
  childRecords: ChildRecords,
  employeeRecords: EmployeeRecords,
  year: number,
  options: Options
) {
  const bv = baseValue(year)
  const qb = qualityBonus(year)
  const ef = expansionFactor(year)
  const bv_per_month = bv / 6 // / 12 months * 2 payments (state, commune)
  const qb_per_month = qb / 12 // only paid by the state

  const withFactorIncrease = options.factorIncrease !== undefined
  let seenThreeIntegrativeChildren = false
  const withDistrictFunding = !!options.districtSubsidy

  const records: FundingRelevantRecord[] = Array.from({ length: 12 })
  for (let i = 0; i < 12; i++) {
    const children = childRecords[i as Month]
    const employees = employeeRecords[i as Month]

    // count the children by the characteristic with the highest subsidy state
    let regelkinder = 0,
      schulkinder = 0,
      migration = 0,
      u3krippe = 0,
      integrativ = 0
    for (const c of children) {
      switch (c.subsidyState) {
        case SubsidyState.X09:
        case SubsidyState.X08:
        case SubsidyState.X07:
        case SubsidyState.X06:
          integrativ++
          break
        case SubsidyState.X05:
        case SubsidyState.X02:
          u3krippe++
          break
        case SubsidyState.X04:
        case SubsidyState.X03:
          migration++
          break
        case SubsidyState.X01:
          schulkinder++
          break
        case SubsidyState.X00:
          regelkinder++
          break
        case SubsidyState.nil:
          console.error(`Unsubsidized child in analysis?`, c)
      }
    }

    if (integrativ !== children.filter((c) => c.assistedIntegration).length) {
      console.warn("Inconsistent number of integrative children!")
    }

    if (integrativ >= 3) {
      seenThreeIntegrativeChildren = true
    }

    const subsidizableChildren = children.filter((c) => c.allocation === Allocation.Foerderfaehig)
    let estimatedSubsidy: number,
      estimatedSubsidyPlusX = NaN,
      plusX = NaN,
      qualityBonus: number

    if (options.rural) {
      const averageTimeFactor =
        subsidizableChildren.reduce((f, c) => f + c.bookingTimeFactor, 0) / 12
      estimatedSubsidy = options.rural * averageTimeFactor * bv_per_month
      qualityBonus = options.rural * averageTimeFactor * qb_per_month
    } else {
      const factor = subsidizableChildren.reduce(
        (f, c) => f + c.subsidyStateFactor * c.bookingTimeFactor,
        0
      )
      estimatedSubsidy = factor * bv_per_month
      qualityBonus = factor * qb_per_month
    }

    if (withFactorIncrease) {
      const factorWithPlusX = subsidizableChildren.reduce(
        (f, c) => f + c.subsidyStateFactorPlusX * c.bookingTimeFactor,
        0
      )
      if (seenThreeIntegrativeChildren) {
        estimatedSubsidyPlusX = factorWithPlusX * bv_per_month
        plusX = estimatedSubsidyPlusX - estimatedSubsidy
      } else {
        estimatedSubsidyPlusX = estimatedSubsidy
        plusX = 0
      }
    }

    const bookingHours = children.reduce((h, c) => h + c.hours, 0)
    const bookingHoursWeighted = children.reduce((h, c) => h + c.subsidyStateFactor * c.hours, 0)
    const bookingHoursWeightedWithoutAssistedIntegration = children.reduce(
      (h, c) => h + c.subsidyStateFactorWithoutAssistedIntegration * c.hours,
      0
    )

    let bookingHoursWeightedPlusX = 0,
      bookingHoursWeightedWithoutAssistedIntegrationPlusX = 0
    if (withFactorIncrease) {
      bookingHoursWeightedPlusX = children.reduce(
        (h, c) => h + c.subsidyStateFactorPlusX * c.hours,
        0
      )
      bookingHoursWeightedWithoutAssistedIntegrationPlusX = children.reduce(
        (h, c) => h + c.subsidyStateFactorWithoutAssistedIntegrationPlusX * c.hours,
        0
      )
    }

    let bookingHoursWeightedForDistrict = 0,
      bookingHoursWeightedWithoutAssistedIntegrationForDistrict = 0
    if (options.districtSubsidy === Region.MF) {
      bookingHoursWeightedForDistrict = children.reduce(
        (h, c) =>
          h +
          c.subsidyStateFactor * c.hours +
          (c.districtSubsidy && isIntegrative(c.subsidyState)
            ? RegionMFBookingTimeFactorConstantAddend
            : 0),
        0
      )
      bookingHoursWeightedWithoutAssistedIntegrationForDistrict = children.reduce(
        (h, c) =>
          h +
          c.subsidyStateFactorWithoutAssistedIntegration * c.hours +
          (c.districtSubsidy && isIntegrative(c.subsidyState)
            ? RegionMFBookingTimeFactorConstantAddend
            : 0),
        0
      )
    } else if (withDistrictFunding) {
      bookingHoursWeightedForDistrict = children.reduce(
        (h, c) => h + c.subsidyStateFactorForDistrict * c.hours,
        0
      )
      bookingHoursWeightedWithoutAssistedIntegrationForDistrict = children.reduce(
        (h, c) => h + c.subsidyStateFactorWithoutAssistedIntegrationForDistrict * c.hours,
        0
      )
    }

    const workingHoursPedagocical = employees
      .filter(
        (e) =>
          e.qualification === KibigQualification.Fachkraft ||
          e.qualification === KibigQualification.Ergaenzungskraft
      )
      .reduce((h, c) => h + c.creditableWorkingHours, 0)

    const workingHoursFk = employees
      .filter((e) => e.qualification === KibigQualification.Fachkraft)
      .reduce((h, c) => h + c.creditableWorkingHours, 0)

    const { staffingRatio, minWeeklyHoursFk, fkqPlusMinus, fkq } = calculateRatios(
      bookingHoursWeighted,
      bookingHoursWeightedWithoutAssistedIntegration,
      workingHoursPedagocical,
      workingHoursFk
    )

    const {
      staffingRatio: plusXStaffingRatio,
      minWeeklyHoursFk: plusXMinWeeklyHoursFk,
      fkqPlusMinus: plusXFkPlusMinus,
      fkq: plusXFkq,
    } = withFactorIncrease
      ? calculateRatios(
          bookingHoursWeightedPlusX,
          bookingHoursWeightedWithoutAssistedIntegrationPlusX,
          workingHoursPedagocical,
          workingHoursFk
        )
      : { staffingRatio: NaN, minWeeklyHoursFk: NaN, fkqPlusMinus: NaN, fkq: NaN }

    const {
      staffingRatio: districtStaffingRatio,
      minWeeklyHoursFk: districtMinWeeklyHoursFk,
      fkqPlusMinus: _districtFkPlusMinus,
      fkq: districtFkq,
    } = withDistrictFunding
      ? calculateRatios(
          bookingHoursWeightedForDistrict,
          bookingHoursWeightedWithoutAssistedIntegrationForDistrict,
          workingHoursPedagocical,
          workingHoursFk
        )
      : { staffingRatio: NaN, minWeeklyHoursFk: NaN, fkqPlusMinus: NaN, fkq: NaN }

    const preCourseSubsidy =
      children
        .filter((c) => c.preCourse)
        .reduce(
          (s, c) =>
            s +
            (subsidyStateMigration(c.subsidyState)
              ? bv * c.subsidyStateFactor * 0.1
              : bv * c.subsidyStateFactor * 0.4),
          0
        ) / 12

    const u3Children = subsidizableChildren.filter((c) => subsidyStateUnder3(c.subsidyState))

    const u3Subsidy = u3Children.reduce((s, c) => s + bv * c.subsidyStateFactor * 0.15, 0) / 12

    const federalFunds = u3Children.reduce((s, c) => s + c.bookingTimeFactor * bv * ef, 0) / 12

    const parentalFeeSubsidies = children.reduce(
      (s, c) => (c.parentalFeeSubsidy ? s + subsidyForParentalFee : s),
      0
    )

    const pctBookingTimeHoursGt3 =
      100 * (children.filter(bookingTimeCategoryGreaterThreeHours).length / children.length) || 0

    const criteria: Criteria[] = []
    if (children.length !== 0 && pctBookingTimeHoursGt3 < 50) {
      criteria.push(Criteria.not_enough_booking_hours)
    }
    if (children.length === 0) {
      criteria.push(Criteria.no_children)
    } else if (withFactorIncrease && !seenThreeIntegrativeChildren) {
      criteria.push(Criteria.not_enough_integrative_children_45x)
    } else if (withDistrictFunding) {
      const hasBeenCalculated = (num: number | string): num is number =>
        typeof num === "number" && !Number.isNaN(num)
      if (
        hasBeenCalculated(districtStaffingRatio) &&
        districtStaffingRatio > minimumStaffingRatio
      ) {
        criteria.push(Criteria.insufficient_as)
      }
      if (hasBeenCalculated(districtFkq) && districtFkq < minimumFkq) {
        criteria.push(Criteria.insufficient_fkq)
      }
    } else {
      if (staffingRatio !== "-" && staffingRatio > minimumStaffingRatio) {
        criteria.push(Criteria.insufficient_as)
      } else if (fkq !== "-" && fkq < minimumFkq) {
        criteria.push(Criteria.insufficient_fkq)
      }
    }

    records[i] = {
      monat: monthLabels[i],
      kinder: children.length,
      regelkinder,
      schulkinder,
      migration,
      u3krippe,
      integrativ,
      anteilgr3: pctBookingTimeHoursGt3,
      bustd: bookingHours,
      bustgew: bookingHoursWeighted,
      "bustgew+x": bookingHoursWeightedPlusX,
      "bustgew5,5": bookingHoursWeightedForDistrict,
      arbstd: workingHoursPedagocical,
      arbstdfk: workingHoursFk,
      midstdfk: minWeeklyHoursFk,
      "midstdfk+x": plusXMinWeeklyHoursFk,
      "midstdfk5,5": districtMinWeeklyHoursFk,
      fkplusminus: fkqPlusMinus,
      "fkplusminus+x": plusXFkPlusMinus,
      fkq: fkq,
      "fkq+x": plusXFkq,
      "fkq5,5": districtFkq,
      as: staffingRatio,
      "as+x": plusXStaffingRatio,
      "as5,5": districtStaffingRatio,
      // Subsidy cuts are dealt with below
      foerderung: MonthSubsidyStatus.SUBSIDIZED,
      foerderbetrag: estimatedSubsidy,
      "+x": plusX,
      "foerderbetrag+x": estimatedSubsidyPlusX,
      vorkurse: preCourseSubsidy,
      zusatzfoerdu3: u3Subsidy,
      qbonus: qualityBonus,
      get foerderungges() {
        return this.foerderbetrag + this.vorkurse + this.zusatzfoerdu3 + this.qbonus
      },
      ebzuschuss: parentalFeeSubsidies,
      basiswert: bv,
      zuschussBundesmittel: federalFunds,
      criteria,
    }
  }

  // Execute order § 17 (3) 4f AVBayKiBiG
  let allowance = UnmetMonthAllowance
  let avgAs = calculateAverage(records, "as")
  if (avgAs > minimumStaffingRatio) {
    console.debug(`Average required AS not met:`, avgAs)
    const sortedByAs = records
      .filter(
        (r) =>
          typeof r.as === "number" &&
          !Number.isNaN(r.as) &&
          typeof r.arbstdfk === "number" &&
          !Number.isNaN(r.arbstdfk)
      )
      .reverse()
      .sort((a, b) => (a.as as unknown as number) - (b.as as unknown as number))
    for (let i = 0; i < 12; i++) {
      const highestAs = sortedByAs.pop()
      if (!highestAs) break
      const month = highestAs.monat
      if (allowance > 0) {
        console.debug(`Month ${month} meets the exception under § 17 (3) 4 AVBayKiBiG`)
        highestAs.foerderung = MonthSubsidyStatus.EXCEPTION_17_3_5
        highestAs.criteria?.push(Criteria.exception_17_3_5)
        allowance--
      } else {
        highestAs.foerderung = MonthSubsidyStatus.NO_SUBSIDY
      }
      avgAs = calculateAverage(sortedByAs, "as")
      console.debug(`New average AS after dropping month ${month}: `, avgAs)
      if (avgAs <= minimumStaffingRatio) {
        break // new yearly average is good
      }
    }
  }

  const avgMinFk = calculateAverage(records, "midstdfk")
  let avgFkHours = calculateAverage(records, "arbstdfk")
  if (avgFkHours < avgMinFk) {
    console.debug(`Average fkHours not met:`, avgFkHours)
    const sortedByFkDiff = records
      .filter(
        (r) =>
          typeof r.as === "number" &&
          !Number.isNaN(r.as) &&
          typeof r.arbstdfk === "number" &&
          !Number.isNaN(r.arbstdfk)
      )
      .reverse()
      .sort((a, b) => (a.arbstdfk as unknown as number) - (b.arbstdfk as unknown as number))
    for (let i = 0; i < 12; i++) {
      const highestFkqDiff = sortedByFkDiff.pop()
      if (!highestFkqDiff) break
      const month = highestFkqDiff.monat
      if (allowance > 0) {
        console.debug(`Month ${month} meets the exception under § 17 (3) 4 AVBayKiBiG`)
        highestFkqDiff.foerderung = MonthSubsidyStatus.EXCEPTION_17_3_5
        highestFkqDiff.criteria?.push(Criteria.exception_17_3_5)
        allowance--
      } else {
        highestFkqDiff.foerderung = MonthSubsidyStatus.NO_SUBSIDY
      }
      avgFkHours = calculateAverage(sortedByFkDiff, "arbstdfk")
      console.debug(`New avgFkHours after dropping month ${month}: `, avgFkHours)
      if (avgFkHours >= avgMinFk) {
        break // new yearly average is good
      }
    }
  }

  if (avgAs > minimumStaffingRatio || avgFkHours < avgMinFk) {
    console.info(
      `Average AS (${avgAs}) or fkHours (${avgFkHours}) still too low after application of § 17 (3) 4f AVBayKiBiG!`
    )
    // -> no subsidy at all for this year
    records.forEach((r) => {
      r.foerderung = MonthSubsidyStatus.NO_SUBSIDY
    })
  }

  // § 20 (2) AVBayKiBiG
  records.forEach((r) => {
    if (r.foerderung === MonthSubsidyStatus.NO_SUBSIDY) {
      r.qbonus = 0
    }
  })

  return records
}

type Ratios = {
  staffingRatio: number | "-"
  minWeeklyHoursFk: number | "-"
  fkqPlusMinus: number | "-"
  fkq: number | "-"
}
function calculateRatios(
  bookingHoursWeighted: number,
  bookingHoursWeightedWithoutAssistedIntegration: number,
  workingHoursPedagocical: number,
  workingHoursFk: number
): Ratios {
  // AS = (BuchungsstundenGew * 5) / Arbeitsstunden des Päd. Personals
  const staffingRatio =
    bookingHoursWeighted === 0 || workingHoursPedagocical === 0
      ? "-"
      : (bookingHoursWeighted * 5) / workingHoursPedagocical
  // MinPersonalstunden = (BuchungsstundenGew * 5) / MinAS
  // Note: Confirmed that we don't use this value (we check the MinAS directly)
  const _minWeeklyHours = (bookingHoursWeighted * 5) / minimumStaffingRatio
  // MinPersonalstundenFachkräfte = ((BuchungsstundenGewOhneIntegration * 5) / MinAS) / 2
  const minWeeklyHoursFk =
    bookingHoursWeightedWithoutAssistedIntegration === 0
      ? "-"
      : (bookingHoursWeightedWithoutAssistedIntegration * 5) / minimumStaffingRatio / 2
  const fkqPlusMinus = minWeeklyHoursFk === "-" ? "-" : workingHoursFk - minWeeklyHoursFk
  // FKQ(%) = (PersonalstundenFachkräfte / MinPersonalstundenFachkräfte) * 50
  const fkq =
    minWeeklyHoursFk === "-" || minWeeklyHoursFk === 0
      ? "-"
      : (workingHoursFk / minWeeklyHoursFk) * 50 // as a percentage
  return {
    staffingRatio,
    minWeeklyHoursFk,
    fkqPlusMinus,
    fkq,
  }
}

function calculateAgBrutto(plusX: number, childRecords: ChildRecords, year: number) {
  const { sumBookingTimeFactors, integrativeMonths } = getIntegrativeStats(childRecords)
  if (integrativeMonths === 0) return Number.NaN
  // AGBrutto = (X-Faktor * SummeBZ * BASISWERT * 4) / (0,8 * IntegrativMonate * 2)
  return (plusX * sumBookingTimeFactors * baseValue(year) * 4) / (0.8 * integrativeMonths * 2)
}

function calculatePlusX(agBrutto: number, childRecords: ChildRecords, year: number) {
  const { sumBookingTimeFactors, integrativeMonths } = getIntegrativeStats(childRecords)
  if (integrativeMonths === 0) return Number.POSITIVE_INFINITY
  // BZFaktorIntegKinder = SummeBZ / IntegrativMonate
  // X-Faktor = ((Arbeitgeberbrutto * 0,8) / (BASISWERT * 2)) / BZFaktorIntegKinder
  const bzFaktor = sumBookingTimeFactors / integrativeMonths
  return (agBrutto * 0.8) / (baseValue(year) * 2) / bzFaktor
}

function getIntegrativeStats(childRecords: ChildRecords): {
  sumBookingTimeFactors: number
  integrativeMonths: number
} {
  let sumBookingTimeFactors = 0
  let integrativeMonths = 0
  let hadMonthWithAtLeastThree = false
  for (let i = 0; i < 12; i++) {
    let integrativeCounter = 0
    let monthSumBookingTimeFactors = 0
    for (const childMonth of childRecords[i as Month]) {
      if (isIntegrative(childMonth.subsidyState)) {
        monthSumBookingTimeFactors += childMonth.bookingTimeFactor
        integrativeCounter++
      }
    }
    if (integrativeCounter >= 3) {
      hadMonthWithAtLeastThree = true
    }
    if (hadMonthWithAtLeastThree) {
      sumBookingTimeFactors += monthSumBookingTimeFactors
      integrativeMonths++
    }
  }

  return {
    sumBookingTimeFactors,
    integrativeMonths,
  }
}

function calculateAverage(records: FundingRelevantRecord[], key: keyof FundingRelevantRecord) {
  let divisor = 0,
    sum = 0
  for (const record of records) {
    if (typeof record[key] === "number") {
      sum += record[key]
      divisor++
    }
  }
  return sum / divisor
}

function calculateSumsAndAverages(
  records: FundingRelevantRecord[],
  label: (typeof TableLabels)[keyof typeof TableLabels],
  info: InfoMessage
): FundingRelevantRecord {
  const averages: Pick<
    FundingRelevantRecord,
    | "kinder"
    | "regelkinder"
    | "schulkinder"
    | "migration"
    | "u3krippe"
    | "integrativ"
    | "anteilgr3"
    | "bustd"
    | "bustgew"
    | "bustgew+x"
    | "bustgew5,5"
    | "arbstd"
    | "arbstdfk"
    | "midstdfk"
    | "midstdfk+x"
    | "midstdfk5,5"
    | "fkplusminus"
    | "fkplusminus+x"
    | "fkq"
    | "fkq+x"
    | "fkq5,5"
    | "as"
    | "as+x"
    | "as5,5"
  > = {
    kinder: 0,
    regelkinder: 0,
    schulkinder: 0,
    migration: 0,
    u3krippe: 0,
    integrativ: 0,
    anteilgr3: 0,
    bustd: 0,
    bustgew: 0,
    "bustgew+x": 0,
    "bustgew5,5": 0,
    arbstd: 0,
    arbstdfk: 0,
    midstdfk: 0,
    "midstdfk+x": 0,
    "midstdfk5,5": 0,
    fkplusminus: 0,
    "fkplusminus+x": 0,
    fkq: 0,
    "fkq+x": 0,
    "fkq5,5": 0,
    as: 0,
    "as+x": 0,
    "as5,5": 0,
  }
  const validMonthsForAveraging = {
    kinder: 0,
    regelkinder: 0,
    schulkinder: 0,
    migration: 0,
    u3krippe: 0,
    integrativ: 0,
    anteilgr3: 0,
    bustd: 0,
    bustgew: 0,
    "bustgew+x": 0,
    "bustgew5,5": 0,
    arbstd: 0,
    arbstdfk: 0,
    midstdfk: 0,
    "midstdfk+x": 0,
    "midstdfk5,5": 0,
    fkplusminus: 0,
    "fkplusminus+x": 0,
    fkq: 0,
    "fkq+x": 0,
    "fkq5,5": 0,
    as: 0,
    "as+x": 0,
    "as5,5": 0,
  }
  const sums = {
    foerderbetrag: 0,
    "+x": 0,
    "foerderbetrag+x": 0,
    vorkurse: 0,
    zusatzfoerdu3: 0,
    qbonus: 0,
    foerderungges: 0,
    ebzuschuss: 0,
    basiswert: 0,
    zuschussBundesmittel: 0,
  }

  for (const record of records) {
    for (const key of Object.keys(averages) as (keyof typeof averages)[]) {
      if (typeof record[key] === "number" && !Number.isNaN(record[key]) && record["kinder"] > 0) {
        averages[key] += record[key]
        validMonthsForAveraging[key]++
      }
    }

    for (const key of Object.keys(sums) as (keyof typeof sums)[]) {
      if (typeof record[key] === "number" && !Number.isNaN(record[key]) && record["kinder"] > 0) {
        sums[key] += record[key]
      }
    }
  }
  for (const key of Object.keys(averages) as (keyof typeof averages)[]) {
    if (
      validMonthsForAveraging[key] === 0 &&
      // can have dashes
      (key === "midstdfk" || key === "fkplusminus" || key === "fkq" || key === "as")
    ) {
      averages[key] = "-"
    } else {
      averages[key] /= validMonthsForAveraging[key]
    }
  }
  return {
    monat: label,
    ...averages,
    foerderung: TableLabels.sum,
    ...sums,
    info,
  }
}

export function kibigChildrenToAnalysisMonths(
  kibigChildren: KibigChild[],
  year: number,
  integrateVacationAndShortTime = false
) {
  const dayOneOfNextYear = dayjs(`${year}`).endOf("year").add(1, "day")
  const months: ChildRecords = monthRecord(() => [])

  for (const child of kibigChildren) {
    // § 187 II 1f BGB (BayKiBiG book: "Zeitpunkt der Vollendung des dritten Lebensjahres")
    const completedYearOfLifeThisYear = calculateAge(child.birthdate, dayOneOfNextYear)
    const childMonths: Record<Month, ChildMonth | undefined> = monthRecord(undefined)
    if (isDev)
      Object.defineProperty(childMonths, "$child", {
        value: child,
      })
    for (const [month, booking] of child.bookings.entries()) {
      if (
        booking.allocation === Allocation.Foerderfaehig ||
        booking.allocation === Allocation.NichtFoerderfaehig
      ) {
        const record: ChildMonth = {
          allocation: booking.allocation,
          hours: BookingTimeHours[booking.bookingTime],
          assistedIntegration: booking.assistedIntegration,
          districtSubsidy: booking.districtSubsidy,
          subsidyState: booking.subsidyState,
          subsidyStateFactor: SubsidyStateFactors[booking.subsidyState],
          subsidyStateFactorPlusX: SubsidyStateFactors[booking.subsidyState],
          subsidyStateFactorForDistrict: SubsidyStateFactors[booking.subsidyState],
          subsidyStateWithoutAssistedIntegration: booking.subsidyStateWithoutAssistedIntegration,
          subsidyStateFactorWithoutAssistedIntegration:
            SubsidyStateFactors[booking.subsidyStateWithoutAssistedIntegration],
          subsidyStateFactorWithoutAssistedIntegrationPlusX:
            SubsidyStateFactors[booking.subsidyStateWithoutAssistedIntegration],
          subsidyStateFactorWithoutAssistedIntegrationForDistrict:
            SubsidyStateFactors[booking.subsidyStateWithoutAssistedIntegration],
          bookingTime: booking.bookingTime,
          bookingTimeFactor: BookingTimeFactors[booking.bookingTime],
          parentalFeeSubsidy:
            // If the booking does not have the exception set to not request this subsidy,
            booking.parentalFeeSubsidy === "1" &&
            // and the child is actually eligible for it this month.
            eligibleForParentalFeeSubsidy(
              completedYearOfLifeThisYear,
              month,
              booking.isSchoolChild
            ),
          preCourse:
            child.preCourse === PreCourse.BothKgj
              ? true
              : child.preCourse === PreCourse.FirstKjg
                ? month < Month.SEPTEMBER
                : child.preCourse === PreCourse.SecondKgj
                  ? month >= Month.SEPTEMBER
                  : false,
        }
        if (isDev) {
          Object.defineProperty(record, "$kibigChild", {
            value: child,
          })
          Object.defineProperty(record, "$kibigMonth", {
            value: booking,
          })
        }
        childMonths[month] = record
        months[month].push(record)
      } else {
        //console.debug(`Skipping non-subsidizable child month ${month}`, child, booking)
      }
    }
    // Overwrite with vacation or short time booking data from EOY / end of bookings backwards
    if (integrateVacationAndShortTime && child.vacationDays >= 15) {
      let n = child.vacationDays >= 45 ? 3 : child.vacationDays >= 30 ? 2 : 1
      for (let month = Month.DECEMBER; month >= Month.JANUARY; month--) {
        const record = childMonths[month]
        if (!record) continue // child does not have a (subsidizable) booking this month
        if (BookingTimeHours[child.vacationBookingTime] > BookingTimeHours[record.bookingTime]) {
          Object.assign(record, {
            hours: BookingTimeHours[child.vacationBookingTime],
            subsidyState: child.vacationSubsidyState,
            subsidyStateFactor: SubsidyStateFactors[child.vacationSubsidyState],
            bookingTime: child.vacationBookingTime,
            bookingTimeFactor: BookingTimeFactors[child.vacationBookingTime],
            $overridden: "vacation",
          })
          if (--n === 0) {
            break
          }
        }
      }
    }
    if (integrateVacationAndShortTime && child.shortTimeDays >= 15) {
      let n = child.shortTimeDays >= 45 ? 3 : child.shortTimeDays >= 30 ? 2 : 1
      for (let month = Month.DECEMBER; month >= Month.JANUARY; month--) {
        const record = childMonths[month]
        if (!record) {
          // child does not have a (subsidizable) booking this month, but the short time booking can become one
          months[month].push({
            allocation: Allocation.KeineBelegung,
            hours: BookingTimeHours[child.shortTimeBookingTime],
            assistedIntegration: false,
            districtSubsidy: false,
            subsidyState: child.shortTimeSubsidyState,
            subsidyStateFactor: SubsidyStateFactors[child.shortTimeSubsidyState],
            subsidyStateWithoutAssistedIntegration: SubsidyState.nil,
            subsidyStateFactorPlusX: 0,
            subsidyStateFactorForDistrict: 0,
            subsidyStateFactorWithoutAssistedIntegration: SubsidyStateFactors[SubsidyState.nil],
            subsidyStateFactorWithoutAssistedIntegrationPlusX: 0,
            subsidyStateFactorWithoutAssistedIntegrationForDistrict: 0,
            bookingTime: child.shortTimeBookingTime,
            bookingTimeFactor: BookingTimeFactors[child.shortTimeBookingTime],
            parentalFeeSubsidy: false,
            preCourse: false,
          })
          if (--n === 0) {
            break
          }
        } else if (
          BookingTimeHours[child.shortTimeBookingTime] > BookingTimeHours[record.bookingTime] &&
          !("$overridden" in record)
        ) {
          Object.assign(record, {
            allocation: Allocation.Foerderfaehig,
            hours: BookingTimeHours[child.shortTimeBookingTime],
            subsidyState: child.shortTimeSubsidyState,
            subsidyStateFactor: SubsidyStateFactors[child.shortTimeSubsidyState],
            bookingTime: child.shortTimeBookingTime,
            bookingTimeFactor: BookingTimeFactors[child.shortTimeBookingTime],
            $overridden: "shortTime",
          })
          if (--n === 0) {
            break
          }
        }
      }
    }
  }
  return months
}

type BookingTimeHourValue = (typeof BookingTimeHours)[keyof typeof BookingTimeHours]
type SubsidyStateFactor = (typeof SubsidyStateFactors)[keyof typeof SubsidyStateFactors]
type BookingTypeFactor = (typeof BookingTimeFactors)[keyof typeof BookingTimeFactors]
type ChildMonth = {
  allocation: Allocation
  hours: BookingTimeHourValue
  assistedIntegration: boolean
  districtSubsidy: boolean
  subsidyState: SubsidyState
  subsidyStateFactor: SubsidyStateFactor
  subsidyStateFactorPlusX: number
  subsidyStateFactorForDistrict: number
  subsidyStateWithoutAssistedIntegration: SubsidyState
  subsidyStateFactorWithoutAssistedIntegration: SubsidyStateFactor
  subsidyStateFactorWithoutAssistedIntegrationPlusX: number
  subsidyStateFactorWithoutAssistedIntegrationForDistrict: number
  bookingTime: BookingTime
  bookingTimeFactor: BookingTypeFactor
  parentalFeeSubsidy: boolean
  preCourse: boolean
}
type EmployeeMonth = {
  creditableWorkingHours: number
  qualification: KibigQualification
}

/** Groups the by-employee KiBiG algorithm results by month for the given year instead */
export function groupKibigEmployeesByMonths(kibigEmployees: EmployeeKibigMonths, year: number) {
  const months: EmployeeRecords = monthRecord(() => [])
  for (const kym of kibigEmployees.values()) {
    for (const [month, data] of Object.entries(kym[year])) {
      const m = (Number.parseInt(month, 10) - 1) as Month
      months[m].push(data)
    }
  }
  return months
}

function bookingTimeCategoryGreaterThreeHours(child: ChildMonth) {
  switch (child.bookingTime) {
    case BookingTime.Y01: // legacy also seems to include Y01 here, so do the same for consistency
    case BookingTime.Y02:
    case BookingTime.Y03:
    case BookingTime.Y04:
    case BookingTime.Y05:
    case BookingTime.Y06:
    case BookingTime.Y07:
    case BookingTime.Y08:
      return true
    case BookingTime.Y00:
    case BookingTime.nil:
      return false
  }
}

function subsidyStateMigration(subsidyState: SubsidyState) {
  switch (subsidyState) {
    case SubsidyState.X03:
    case SubsidyState.X04:
    case SubsidyState.X05:
    case SubsidyState.X09:
      return true
    case SubsidyState.X00:
    case SubsidyState.X01:
    case SubsidyState.X02:
    case SubsidyState.X06:
    case SubsidyState.X07:
    case SubsidyState.X08:
    case SubsidyState.nil:
      return false
  }
}

function subsidyStateUnder3(subsidyState: SubsidyState) {
  switch (subsidyState) {
    case SubsidyState.X02:
    case SubsidyState.X05:
    case SubsidyState.X08:
      return true
    case SubsidyState.X00:
    case SubsidyState.X01:
    case SubsidyState.X03:
    case SubsidyState.X04:
    case SubsidyState.X06:
    case SubsidyState.X07:
    case SubsidyState.X09:
    case SubsidyState.nil:
      return false
  }
}

// Monthly occupancy analysis (Belegung nach Zeitkategorie und Gewichtung)

export const WEIGHTING_COLUMN_KEYS: WeightingKey[] = [
  "regularChild",
  "schoolChild",
  "migration",
  "nurseryChildOrUnder3",
  "assistedIntegration",
]

// i18n keys for occupancy/weighting table column headers
export const WEIGHTING_COLUMN_LABEL_KEYS = [
  "dashboard.weighting.standard_children",
  "dashboard.weighting.pupils",
  "dashboard.weighting.migration",
  "dashboard.weighting.under_3",
  "dashboard.weighting.integration",
  "dashboard.weighting.total",
] as const

const TIME_CATEGORY_ORDER: BookingTimeCategory[] = [
  BookingTimeCategory.One,
  BookingTimeCategory.Two,
  BookingTimeCategory.Three,
  BookingTimeCategory.Four,
  BookingTimeCategory.Five,
  BookingTimeCategory.Six,
  BookingTimeCategory.Seven,
  BookingTimeCategory.Eight,
  BookingTimeCategory.Nine,
  BookingTimeCategory.Ten,
  BookingTimeCategory.Eleven,
  BookingTimeCategory.Twelve,
]

function timeCategoryToRowIndex(category: BookingTimeCategory | null | undefined): number | -1 {
  if (!category || category === BookingTimeCategory.Zero) return -1
  const idx = TIME_CATEGORY_ORDER.indexOf(category)
  return idx >= 0 ? idx : -1
}

export function getMonthBounds(
  year: Year,
  monthIndex: number
): { monthStart: Dayjs; monthEnd: Dayjs } {
  const isCareYear = year.mode === YearMode.Kindergarten
  const startYear = year.start.getFullYear()

  let month: number
  let yearForMonth: number
  if (isCareYear) {
    if (monthIndex < 4) {
      month = monthIndex + Month.SEPTEMBER
      yearForMonth = startYear
    } else {
      month = monthIndex - 4
      yearForMonth = startYear + 1
    }
  } else {
    month = monthIndex
    yearForMonth = startYear
  }

  const monthStart = dayjs(new Date(yearForMonth, month, 1))
  const monthEnd = monthStart.endOf("month")
  return { monthStart, monthEnd }
}

export type MonthlyTableData = {
  rows: number[][]
  columnTotals: number[]
}

export function computeMonthlyTableData(
  children: Child[],
  year: Year,
  monthIndex: number
): MonthlyTableData {
  const { monthStart, monthEnd } = getMonthBounds(year, monthIndex)
  const rows: number[][] = Array(12)
    .fill(0)
    .map(() => [0, 0, 0, 0, 0, 0])
  const columnTotals = [0, 0, 0, 0, 0, 0]

  for (const child of children ?? []) {
    const contracts = child.contracts ?? []
    for (const contract of contracts) {
      const endOfContract = contract.endOfContract ?? monthEnd.toISOString()
      const bookings =
        contract.bookings?.filter(
          (b) =>
            b?.type === BookingTypeEnum.Standard &&
            hasOverlapOrInside(b.from, b.to ?? endOfContract, monthStart, monthEnd)
        ) ?? []

      for (const booking of bookings) {
        const rowIdx = timeCategoryToRowIndex(booking.timeFactor?.category as BookingTimeCategory)
        if (rowIdx < 0) continue

        const weightingKey = getWeightingKey(
          resolveWeightings({
            childBirthDate: child.birth?.date ?? undefined,
            nurseryChild: booking.nurseryChild ?? false,
            migration: booking.migration ?? false,
            assistedIntegration: booking.assistedIntegration ?? false,
            schoolChild: booking.isSchoolChild ?? false,
            monthEnd,
          })
        )
        const colIdx = WEIGHTING_COLUMN_KEYS.indexOf(weightingKey)
        if (colIdx < 0) continue

        rows[rowIdx][colIdx] += 1
        rows[rowIdx][5] += 1
        columnTotals[colIdx] += 1
        columnTotals[5] += 1
      }
    }
  }

  return { rows, columnTotals }
}
